'use client'

import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { UserPlus, AlertCircle, CheckCircle, Users } from 'lucide-react'
import { api } from '@/lib/api'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useTeamsStore } from '@/stores/teams'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { Team } from '@/types/team'
import { derivePrimaryTeamId, normalizeTeamIds } from '@/lib/utils/teams'
import type { SupervisorAvailableAgent } from '@/components/agent/SupervisorManageDialog'

interface TransferModalProps {
  sessionId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onTransferComplete: () => void
  canTransfer: boolean
}

export function TransferModal({
  sessionId,
  open,
  onOpenChange,
  onTransferComplete,
  canTransfer,
}: TransferModalProps) {
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null)
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null)
  const [priority, setPriority] = useState<
    'low' | 'normal' | 'high' | 'urgent'
  >('normal')
  const [reason, setReason] = useState('')
  const [isTransferring, setIsTransferring] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [handoverRequestId, setHandoverRequestId] = useState<number | null>(
    null
  )
  const [lastTransferWasAgent, setLastTransferWasAgent] = useState<
    boolean | null
  >(null)
  const queryClient = useQueryClient()
  const storeTeams = useTeamsStore(state => state.teamFilter.availableTeams)
  const fetchTeams = useTeamsStore(state => state.fetchTeams)

  const availableAgentsKey = useMemo(
    () => ['sessions', sessionId, 'transfer', 'available-agents'] as const,
    [sessionId]
  )

  const availableTeamsKey = useMemo(
    () => ['teams', 'available-for-transfer'] as const,
    []
  )

  const {
    data: transferAgents,
    isFetching: isLoadingAgents,
    error: agentsError,
    refetch: refetchAgents,
  } = useQuery<SupervisorAvailableAgent[]>({
    queryKey: availableAgentsKey,
    enabled: open && canTransfer,
    queryFn: async () => {
      const response = await api.agent.listAgents()

      if (!response.success) {
        throw new Error(response.error || 'Failed to load available agents')
      }

      const rawAgents = Array.isArray(response.data)
        ? (response.data as any[])
        : []

      const processedAgents = rawAgents
        .map(rawAgent => {
          const fallbackTeamId =
            (rawAgent as any)?.primaryTeamId ??
            (rawAgent as any)?.teamId ??
            null
          const normalizedTeamIds = normalizeTeamIds(
            (rawAgent as any)?.teamIds,
            fallbackTeamId
          )
          const primaryTeamId = derivePrimaryTeamId(
            normalizedTeamIds,
            fallbackTeamId
          )

          const parsedIdValue =
            typeof (rawAgent as any)?.id === 'number'
              ? (rawAgent as any)?.id
              : (rawAgent as any)?.id != null
                ? Number.parseInt(String((rawAgent as any)?.id), 10)
                : null

          if (parsedIdValue == null || Number.isNaN(parsedIdValue)) {
            return null
          }

          return {
            id: parsedIdValue,
            username: (rawAgent as any)?.username ?? `Agent #${parsedIdValue}`,
            firstName: (rawAgent as any)?.firstName ?? null,
            lastName: (rawAgent as any)?.lastName ?? null,
            isOnline:
              typeof (rawAgent as any)?.isOnline === 'boolean'
                ? Boolean((rawAgent as any)?.isOnline)
                : null,
            teamIds: normalizedTeamIds,
            primaryTeamId,
            teamId:
              primaryTeamId ??
              ((rawAgent as any)?.teamId != null
                ? Number.parseInt(String((rawAgent as any)?.teamId), 10)
                : null),
            teamName: (rawAgent as any)?.teamName ?? null,
          } as SupervisorAvailableAgent
        })
        .filter(
          (agent): agent is SupervisorAvailableAgent =>
            agent != null && agent.id != null
        )

      const seenIds = new Set<number>()
      const seenUsernames = new Set<string>()

      return processedAgents.filter(agent => {
        const normalizedUsername =
          typeof agent.username === 'string'
            ? agent.username.trim().toLowerCase()
            : null

        if (agent.id != null) {
          const numericId = Number(agent.id)
          if (Number.isFinite(numericId)) {
            if (seenIds.has(numericId)) {
              return false
            }
            seenIds.add(numericId)
            if (normalizedUsername) {
              seenUsernames.add(normalizedUsername)
            }
            return true
          }
        }

        if (normalizedUsername) {
          if (seenUsernames.has(normalizedUsername)) {
            return false
          }
          seenUsernames.add(normalizedUsername)
          return true
        }

        return true
      })
    },
  })

  const {
    data: availableTeamsData,
    isFetching: isLoadingTeams,
    error: teamsError,
    refetch: refetchTeams,
  } = useQuery<Team[]>({
    queryKey: availableTeamsKey,
    enabled: open && canTransfer,
    queryFn: async () => {
      const response = await api.teams.list()

      if (!response.success) {
        throw new Error(response.error || 'Failed to load teams')
      }

      if (!Array.isArray(response.data)) {
        return []
      }

      return response.data
        .filter((team: Team) => team.isActive)
        .sort((a: Team, b: Team) => {
          const aOnline = a.onlineAgentCount ?? 0
          const bOnline = b.onlineAgentCount ?? 0
          if (aOnline !== bOnline) {
            return bOnline - aOnline
          }

          const nameA = (a.name ?? '').toLowerCase()
          const nameB = (b.name ?? '').toLowerCase()
          return nameA.localeCompare(nameB)
        })
    },
  })

  const availableAgents = useMemo(() => transferAgents ?? [], [transferAgents])
  const transferTeams = useMemo(
    () => availableTeamsData ?? [],
    [availableTeamsData]
  )

  const teamsForSelection = useMemo(() => {
    const map = new Map<number, Team>()
    storeTeams.forEach(team => {
      if (team.isActive) {
        map.set(team.id, team)
      }
    })
    transferTeams.forEach(team => {
      if (team.isActive) {
        map.set(team.id, team)
      }
    })
    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    )
  }, [storeTeams, transferTeams])

  const teamNameMap = useMemo(() => {
    return new Map(teamsForSelection.map(team => [team.id, team.name]))
  }, [teamsForSelection])

  const normalizedAgents = useMemo(() => {
    if (!availableAgents.length) {
      return [] as SupervisorAvailableAgent[]
    }

    return availableAgents
      .map(agent => {
        const normalizedTeamIds = normalizeTeamIds(
          agent.teamIds,
          agent.primaryTeamId ?? agent.teamId
        )
        const primaryTeamId = derivePrimaryTeamId(
          normalizedTeamIds,
          agent.primaryTeamId ?? agent.teamId
        )

        return {
          ...agent,
          teamIds: normalizedTeamIds,
          primaryTeamId,
          teamId: primaryTeamId ?? null,
          teamName:
            agent.teamName ??
            (primaryTeamId != null
              ? (teamNameMap.get(primaryTeamId) ?? null)
              : null),
        }
      })
      .sort((a, b) => {
        const onlineDiff =
          Number(Boolean(b.isOnline)) - Number(Boolean(a.isOnline))
        if (onlineDiff !== 0) {
          return onlineDiff
        }

        const nameA = (
          a.firstName && a.lastName
            ? `${a.firstName} ${a.lastName}`
            : a.username || ''
        ).toLowerCase()
        const nameB = (
          b.firstName && b.lastName
            ? `${b.firstName} ${b.lastName}`
            : b.username || ''
        ).toLowerCase()
        return nameA.localeCompare(nameB)
      })
  }, [availableAgents, teamNameMap])

  const filteredAgents = useMemo(() => {
    if (selectedTeamId == null) {
      return normalizedAgents
    }

    return normalizedAgents.filter(agent => {
      if (Array.isArray(agent.teamIds) && agent.teamIds.length > 0) {
        return agent.teamIds.includes(selectedTeamId)
      }
      if (agent.primaryTeamId != null) {
        return agent.primaryTeamId === selectedTeamId
      }
      if (agent.teamId != null) {
        return agent.teamId === selectedTeamId
      }
      return false
    })
  }, [normalizedAgents, selectedTeamId])

  const filteredAgentsOnlineCount = useMemo(
    () => filteredAgents.filter(agent => Boolean(agent.isOnline)).length,
    [filteredAgents]
  )

  const selectedAgent = useMemo(
    () => normalizedAgents.find(agent => agent.id === selectedAgentId) ?? null,
    [normalizedAgents, selectedAgentId]
  )

  const canSubmitTransfer = useMemo(() => {
    if (
      !canTransfer ||
      selectedTeamId == null ||
      teamsForSelection.length === 0
    ) {
      return false
    }

    if (selectedAgentId == null) {
      return true
    }

    return filteredAgents.some(agent => agent.id === selectedAgentId)
  }, [
    canTransfer,
    filteredAgents,
    selectedAgentId,
    selectedTeamId,
    teamsForSelection.length,
  ])

  const isSubmitDisabled =
    !canSubmitTransfer ||
    isTransferring ||
    isLoadingTeams ||
    (selectedAgentId != null && isLoadingAgents)

  const selectedTeam = useMemo(
    () => teamsForSelection.find(team => team.id === selectedTeamId) ?? null,
    [teamsForSelection, selectedTeamId]
  )

  useEffect(() => {
    if (open && !canTransfer) {
      setError('You do not have permission to transfer sessions.')
      setIsTransferring(false)
      setIsCancelling(false)
    }

    if (open) {
      setSelectedAgentId(null)
      setSelectedTeamId(null)
      setPriority('normal')
      setReason('')
      setError(null)
      setSuccess(false)
      setHandoverRequestId(null)
      setLastTransferWasAgent(null)
      setIsCancelling(false)
    } else {
      queryClient.removeQueries({ queryKey: availableAgentsKey })
      queryClient.removeQueries({ queryKey: availableTeamsKey })
    }
  }, [availableAgentsKey, availableTeamsKey, open, canTransfer, queryClient])

  useEffect(() => {
    if (!open || !canTransfer) {
      return
    }

    if (storeTeams.length === 0) {
      fetchTeams().catch(fetchError => {
        console.error('Failed to load teams for transfer modal:', fetchError)
      })
    }
  }, [fetchTeams, open, storeTeams.length, canTransfer])

  useEffect(() => {
    if (agentsError instanceof Error) {
      console.error('Error loading available agents:', agentsError)
      setError(agentsError.message || 'Failed to load available agents')
    }
  }, [agentsError])

  useEffect(() => {
    if (!open) {
      return
    }

    if (teamsForSelection.length === 0) {
      setSelectedTeamId(null)
      return
    }

    if (
      selectedTeamId != null &&
      teamsForSelection.some(team => team.id === selectedTeamId)
    ) {
      return
    }

    setSelectedTeamId(teamsForSelection[0].id)
  }, [open, teamsForSelection, selectedTeamId])

  useEffect(() => {
    if (!open) {
      return
    }

    if (
      selectedAgentId != null &&
      !filteredAgents.some(agent => agent.id === selectedAgentId)
    ) {
      setSelectedAgentId(null)
    }
  }, [filteredAgents, selectedAgentId, open])

  useEffect(() => {
    if (teamsError instanceof Error) {
      console.error('Error loading teams:', teamsError)
      setError(teamsError.message || 'Failed to load teams')
    }
  }, [teamsError])

  const handleTransfer = async () => {
    if (!canTransfer) {
      setError('You do not have permission to transfer sessions.')
      return
    }

    if (!selectedTeamId) {
      setError('Please select a team')
      return
    }

    const isAgentTransfer = selectedAgentId != null
    if (isAgentTransfer && !selectedAgentId) {
      setError('Please select an agent')
      return
    }

    try {
      setIsTransferring(true)
      setError(null)

      let transferResponse

      if (!isAgentTransfer) {
        transferResponse = await api.sessions.transferSessionToTeam(
          sessionId,
          selectedTeamId!,
          reason.trim() || undefined,
          priority
        )
      } else {
        console.log('[TransferModal] Transferring to agent with params:', {
          sessionId,
          targetAgentId: selectedAgentId,
          reason: reason.trim() || undefined,
          priority,
          teamId: selectedTeamId ?? undefined,
          selectedTeamIdRaw: selectedTeamId,
        })
        transferResponse = await api.sessions.transferSession(
          sessionId,
          selectedAgentId!,
          reason.trim() || undefined,
          priority,
          selectedTeamId ?? undefined
        )
      }

      if (!transferResponse.success) {
        throw new Error(
          transferResponse.error || 'Failed to send transfer request'
        )
      }

      setLastTransferWasAgent(isAgentTransfer)
      setSuccess(true)
      setHandoverRequestId(transferResponse.data?.handoverRequestId || null)
    } catch (err: any) {
      console.error('Error transferring session:', err)
      setError(err?.message || 'Failed to send transfer request')
    } finally {
      setIsTransferring(false)
    }
  }

  const handleCancelTransfer = async () => {
    if (!canTransfer) {
      setError('You do not have permission to manage transfers.')
      return
    }

    if (!handoverRequestId) {
      setError('No transfer request to cancel')
      return
    }

    try {
      setIsCancelling(true)
      setError(null)

      const cancelResponse = await api.sessions.cancelTransfer(
        sessionId,
        handoverRequestId
      )

      if (!cancelResponse.success) {
        throw new Error(
          cancelResponse.error || 'Failed to cancel transfer request'
        )
      }

      setSuccess(false)
      setHandoverRequestId(null)
      setSelectedAgentId(null)
      setSelectedTeamId(null)
      setPriority('normal')
      setReason('')
      setLastTransferWasAgent(null)

      onTransferComplete()
      if (lastTransferWasAgent) {
        await refetchAgents()
      }
      await refetchTeams()
    } catch (err: any) {
      console.error('Error cancelling transfer:', err)
      setError(err?.message || 'Failed to cancel transfer request')
    } finally {
      setIsCancelling(false)
    }
  }

  const handleClose = () => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Transfer Session</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {!canTransfer ? (
            <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
              <p className="font-medium">Transfer unavailable</p>
              <p className="mt-2">
                You do not have permission to transfer sessions. Please contact
                a supervisor if you believe this is an error.
              </p>
            </div>
          ) : success ? (
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-blue-500 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">
                Transfer Request Sent!
              </h4>
              <p className="text-gray-600 mb-6">
                {lastTransferWasAgent
                  ? `Transfer request has been sent to ${selectedAgent?.username ?? 'the selected agent'}. They need to accept the request before the session is transferred.`
                  : `Transfer request has been sent to ${selectedTeam?.name ?? 'the selected'} team. Any agent from this team can accept the request.`}
              </p>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm text-red-700">{error}</span>
                </div>
              )}
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm text-red-700">{error}</span>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="transfer-team-select">Select Team *</Label>
                {isLoadingTeams ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-sm text-gray-500 mt-2">
                      Loading teams...
                    </p>
                  </div>
                ) : (
                  <Select
                    value={
                      selectedTeamId != null ? String(selectedTeamId) : '__none'
                    }
                    onValueChange={value => {
                      if (!value || value === '__none') {
                        setSelectedTeamId(null)
                        return
                      }
                      const numeric = Number.parseInt(value, 10)
                      if (Number.isNaN(numeric)) {
                        setSelectedTeamId(null)
                        return
                      }
                      setSelectedTeamId(numeric)
                    }}
                    disabled={
                      isTransferring ||
                      teamsForSelection.length === 0 ||
                      !canTransfer
                    }
                  >
                    <SelectTrigger id="transfer-team-select">
                      <SelectValue
                        placeholder={
                          teamsForSelection.length === 0
                            ? 'No active teams available'
                            : 'Choose a team'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">-- Select team --</SelectItem>
                      {teamsForSelection.map(team => (
                        <SelectItem key={team.id} value={String(team.id)}>
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center">
                              <Users className="w-4 h-4 mr-2" />
                              {team.name}
                              {team.description && (
                                <span className="text-gray-500 ml-2 text-xs">
                                  - {team.description}
                                </span>
                              )}
                            </div>
                            <span
                              className={`text-xs font-medium ${
                                (team.onlineAgentCount ?? 0) > 0
                                  ? 'text-green-600'
                                  : 'text-gray-400'
                              }`}
                            >
                              {(team.onlineAgentCount ?? 0) > 0
                                ? `${team.onlineAgentCount} online`
                                : 'All offline'}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <p className="text-xs text-gray-500">
                  Choose which team should take ownership. Leaving the agent
                  selection below on &quot;All agents&quot; will broadcast to
                  the entire team.
                </p>
                {selectedTeam && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-start">
                      <Users className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="font-medium text-blue-900">
                          {selectedTeam.name}
                        </div>
                        {selectedTeam.description && (
                          <div className="text-sm text-blue-700 mt-1">
                            {selectedTeam.description}
                          </div>
                        )}
                        <div className="text-xs text-blue-600 mt-2">
                          {selectedTeam.onlineAgentCount &&
                          selectedTeam.onlineAgentCount > 0
                            ? `ⓘ ${selectedTeam.onlineAgentCount} agent${selectedTeam.onlineAgentCount > 1 ? 's' : ''} currently online can accept immediately.`
                            : 'ⓘ All agents are currently offline, but the team will be notified to pick this up when they return.'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {selectedTeamId != null && (
                <div className="space-y-2">
                  <Label htmlFor="transfer-agent-select">Select Agent</Label>
                  <Select
                    value={
                      selectedAgentId != null
                        ? String(selectedAgentId)
                        : '__all'
                    }
                    onValueChange={value => {
                      if (!value || value === '__all') {
                        setSelectedAgentId(null)
                        return
                      }
                      if (value === '__loading' || value === '__empty') {
                        return
                      }
                      const numeric = Number.parseInt(value, 10)
                      if (Number.isNaN(numeric)) {
                        setSelectedAgentId(null)
                        return
                      }
                      setSelectedAgentId(numeric)
                    }}
                    disabled={
                      isTransferring ||
                      !canTransfer ||
                      filteredAgents.length === 0
                    }
                  >
                    <SelectTrigger id="transfer-agent-select">
                      <SelectValue placeholder="All agents (broadcast)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all">
                        All agents (broadcast)
                      </SelectItem>
                      {isLoadingAgents ? (
                        <SelectItem value="__loading" disabled>
                          Loading agents...
                        </SelectItem>
                      ) : filteredAgents.length > 0 ? (
                        filteredAgents.map(agent => {
                          const fullName =
                            agent.firstName && agent.lastName
                              ? `${agent.firstName} ${agent.lastName}`
                              : agent.username
                          const usernamePart =
                            agent.firstName && agent.lastName
                              ? ` (${agent.username})`
                              : ''
                          const statusLabel =
                            agent.isOnline == null
                              ? ''
                              : agent.isOnline
                                ? ' · Online'
                                : ' · Offline'

                          return (
                            <SelectItem key={agent.id} value={String(agent.id)}>
                              {`${fullName}${usernamePart}${statusLabel}`}
                            </SelectItem>
                          )
                        })
                      ) : (
                        <SelectItem value="__empty" disabled>
                          No agents available in this team
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    {selectedAgentId == null
                      ? filteredAgents.length > 0
                        ? filteredAgentsOnlineCount > 0
                          ? `Broadcast to every agent in the selected team. ${filteredAgentsOnlineCount} ${filteredAgentsOnlineCount === 1 ? 'agent is' : 'agents are'} online right now.`
                          : 'Broadcast to every agent in the selected team. Everyone is currently offline, but they will be notified when they come online.'
                        : 'No agents are assigned to this team yet.'
                      : 'Assign directly to the selected agent.'}
                  </p>
                  {agentsError instanceof Error && (
                    <p className="text-xs text-red-600">
                      {agentsError.message ?? 'Failed to load agents'}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="priority">Priority *</Label>
                <Select
                  value={priority}
                  onValueChange={(
                    value: 'low' | 'normal' | 'high' | 'urgent'
                  ) => setPriority(value)}
                  disabled={isTransferring || !canTransfer}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">
                      <div className="flex items-center">
                        <div className="w-2 h-2 rounded-full bg-gray-400 mr-2"></div>
                        Low
                      </div>
                    </SelectItem>
                    <SelectItem value="normal">
                      <div className="flex items-center">
                        <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                        Normal
                      </div>
                    </SelectItem>
                    <SelectItem value="high">
                      <div className="flex items-center">
                        <div className="w-2 h-2 rounded-full bg-orange-500 mr-2"></div>
                        High
                      </div>
                    </SelectItem>
                    <SelectItem value="urgent">
                      <div className="flex items-center">
                        <div className="w-2 h-2 rounded-full bg-red-500 mr-2"></div>
                        Urgent
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason">Reason for Transfer</Label>
                <Textarea
                  id="reason"
                  value={reason}
                  onChange={event => setReason(event.target.value)}
                  placeholder="Please explain why you're transferring this session..."
                  rows={3}
                  maxLength={500}
                  disabled={isTransferring || !canTransfer}
                />
                <p className="text-xs text-gray-500">
                  {reason.length}/500 characters
                </p>
              </div>
            </>
          )}
        </div>

        {!canTransfer ? (
          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
          </DialogFooter>
        ) : success ? (
          <DialogFooter>
            <Button
              variant="destructive"
              onClick={handleCancelTransfer}
              disabled={isCancelling || !canTransfer}
            >
              {isCancelling ? 'Cancelling...' : 'Cancel Transfer'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                onTransferComplete()
                handleClose()
              }}
              disabled={isCancelling}
            >
              Close
            </Button>
          </DialogFooter>
        ) : (
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isTransferring}
            >
              Cancel
            </Button>
            <Button onClick={handleTransfer} disabled={isSubmitDisabled}>
              {isTransferring ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Transferring...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Transfer Session
                </>
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
